import React, { useEffect, useMemo, useState } from 'react';
import { getAuth } from 'firebase/auth';
import {
  collection,
  addDoc,
  serverTimestamp,
  doc,
  updateDoc,
  onSnapshot,
  query,
  orderBy,
  limit,
  deleteDoc,
  getCountFromServer,
} from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { Apple, PlaySquare, Fan, MessageSquare } from 'lucide-react';
import firebase from '../db/firebase/firebase'; 

const OS_OPTIONS = [
  { key: 'ios', label: 'iOS' },
  { key: 'android', label: 'Android' },
  { key: 'admin_panel', label: 'Admin Panel' },
];

const PRIMARY = '#5297A8';

const BugReport = () => {
  const [openSheet, setOpenSheet] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Form state
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [osSelected, setOsSelected] = useState([]);
  const [phoneModel, setPhoneModel] = useState('');

  // Media state
  const [files, setFiles] = useState([]); // imágenes
  const [video, setVideo] = useState(null); // File | null

  // Previews
  const [imagePreviews, setImagePreviews] = useState([]);
  const [videoPreview, setVideoPreview] = useState(null);

  // Lista global de reportes
  const [reports, setReports] = useState([]);

  // Mapa de conteo de comentarios por reporte
  const [commentCounts, setCommentCounts] = useState({}); // { [reportId]: number }

  // Modal de detalle + comentarios
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [activeReport, setActiveReport] = useState(null); // {id, ...data}
  const [comments, setComments] = useState([]); // [{id, ...data}]
  const [newComment, setNewComment] = useState('');
  const [sendingComment, setSendingComment] = useState(false);

  // Modal de confirmación para borrar comentario
  const [confirmDelete, setConfirmDelete] = useState(null); // {commentId, authorUid} | null
  const [deletingComment, setDeletingComment] = useState(false);

  const auth = getAuth();
  const user = auth.currentUser;

  // Bloquear scroll del body si hay modal/sheet abierto
  useEffect(() => {
    const open = openSheet || detailsOpen || !!confirmDelete;
    if (open) {
      const prev = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      return () => { document.body.style.overflow = prev; };
    }
  }, [openSheet, detailsOpen, confirmDelete]);

  // Submit habilitado si hay mínimos y al menos una media
  const canSubmit = useMemo(() => {
    return !!(title.trim() && description.trim() && osSelected.length > 0);
  }, [title, description, osSelected]);

  const resetForm = () => {
    setTitle('');
    setDescription('');
    setOsSelected([]);
    setPhoneModel('');
    setFiles([]);
    setVideo(null);
  };

  const toggleOS = (key) => {
    setOsSelected((prev) =>
      prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]
    );
  };

  const handleFilesChange = (e) => {
    const list = Array.from(e.target.files || []);
    const imagesOnly = list.filter((f) => f.type.startsWith('image/'));
    setFiles(imagesOnly);
  };

  const handleVideoChange = (e) => {
    const f = (e.target.files && e.target.files[0]) || null;
    if (f && f.type.startsWith('video/')) {
      setVideo(f);
    } else {
      setVideo(null);
    }
  };

  // Previews imágenes (cleanup)
  useEffect(() => {
    imagePreviews.forEach((url) => URL.revokeObjectURL(url));
    const urls = files.map((f) => URL.createObjectURL(f));
    setImagePreviews(urls);
    return () => { urls.forEach((url) => URL.revokeObjectURL(url)); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [files]);

  // Preview video (cleanup)
  useEffect(() => {
    if (videoPreview) {
      URL.revokeObjectURL(videoPreview);
      setVideoPreview(null);
    }
    if (video) {
      const url = URL.createObjectURL(video);
      setVideoPreview(url);
      return () => { URL.revokeObjectURL(url); };
    }
  }, [video]); // eslint-disable-line

  const removeImageAt = (idx) => {
    const next = files.slice();
    next.splice(idx, 1);
    setFiles(next);
  };

  const clearVideo = () => setVideo(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canSubmit || submitting) return;

    try {
      setSubmitting(true);

      const colRef = collection(firebase.db, 'reportsBugs');
      const basePayload = {
        title: title.trim(),
        description: description.trim(),
        operatingSystems: osSelected, // ['ios', 'android', 'admin_panel']
        phoneModel: phoneModel.trim() || '',
        inTestflight: false,
        inPlayStore: false,
        inAppleStore: false,
        solved: false,
        comments: '', // placeholder legacy
        createdAt: serverTimestamp(),
        userEmail: user?.email || '',
        userUid: user?.uid || '',
        imageUrls: [],
        videoUrl: '',
      };

      const docRef = await addDoc(colRef, basePayload);
      const docId = docRef.id;

      const imageUrls = [];
      let videoUrl = '';

      // Subir imágenes
      if (files.length > 0) {
        for (let i = 0; i < files.length; i++) {
          const file = files[i];
          const storageRef = ref(
            firebase.storage,
            `bugReports/${docId}/images/${Date.now()}_${i}_${file.name}`
          );
          await uploadBytes(storageRef, file);
          const url = await getDownloadURL(storageRef);
          imageUrls.push(url);
        }
      }

      // Subir video
      if (video) {
        const vRef = ref(
          firebase.storage,
          `bugReports/${docId}/video/${Date.now()}_${video.name}`
        );
        await uploadBytes(vRef, video);
        videoUrl = await getDownloadURL(vRef);
      }

      await updateDoc(doc(firebase.db, 'reportsBugs', docId), { imageUrls, videoUrl });

      resetForm();
      setOpenSheet(false);
      alert('Bug report submitted successfully.');
    } catch (err) {
      console.error(err);
      alert('There was an error submitting the bug report.');
    } finally {
      setSubmitting(false);
    }
  };

  // Suscribirse a TODOS los reportes (global)
  useEffect(() => {
    const q = query(
      collection(firebase.db, 'reportsBugs'),
      orderBy('createdAt', 'desc'),
      limit(50) // ajustá el límite a gusto
    );
    const unsub = onSnapshot(q, (snap) => {
      const arr = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      setReports(arr);
    });
    return () => unsub();
  }, []);

  // Cargar contador de comentarios por cada reporte listado
  useEffect(() => {
    let canceled = false;
    const loadCounts = async () => {
      try {
        const entries = await Promise.all(
          reports.map(async (r) => {
            const cRef = collection(firebase.db, 'reportsBugs', r.id, 'comments');
            const snapshot = await getCountFromServer(cRef);
            return [r.id, snapshot.data().count || 0];
          })
        );
        if (!canceled) {
          setCommentCounts(Object.fromEntries(entries));
        }
      } catch (e) {
        console.error('Error fetching comment counts', e);
      }
    };
    if (reports.length > 0) loadCounts();
    return () => { canceled = true; };
  }, [reports]);

  
  useEffect(() => {
    let unsub = null;
    if (detailsOpen && activeReport?.id) {
      const commentsRef = collection(firebase.db, 'reportsBugs', activeReport.id, 'comments');
      const qc = query(commentsRef, orderBy('createdAt', 'asc'));
      unsub = onSnapshot(qc, (snap) => {
        const arr = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        setComments(arr);
       
        setCommentCounts((prev) => ({ ...prev, [activeReport.id]: arr.length }));
      });
    }
    return () => { if (unsub) unsub(); };
  }, [detailsOpen, activeReport?.id]);

  const openDetailsFor = (report) => {
    setActiveReport(report);
    setDetailsOpen(true);
  };

  const sendComment = async () => {
    if (!activeReport?.id || !newComment.trim() || sendingComment) return;
    try {
      setSendingComment(true);
      const commentsRef = collection(firebase.db, 'reportsBugs', activeReport.id, 'comments');
      await addDoc(commentsRef, {
        text: newComment.trim(),
        authorEmail: user?.email || '',
        authorUid: user?.uid || '',
        createdAt: serverTimestamp(),
      });
      setNewComment('');
    } catch (e) {
      console.error(e);
      alert('Error sending comment');
    } finally {
      setSendingComment(false);
    }
  };

  const requestDeleteComment = (commentId, commentAuthorUid) => {
    if (!user?.uid || user.uid !== commentAuthorUid) return; // doble check
    setConfirmDelete({ commentId, authorUid: commentAuthorUid });
  };

  const confirmDeleteComment = async () => {
    if (!activeReport?.id || !confirmDelete?.commentId || deletingComment) return;
    try {
      setDeletingComment(true);
      await deleteDoc(
        doc(firebase.db, 'reportsBugs', activeReport.id, 'comments', confirmDelete.commentId)
      );
      setConfirmDelete(null);
    } catch (e) {
      console.error(e);
      alert('Error deleting comment.');
    } finally {
      setDeletingComment(false);
    }
  };

  return (
    <div className="flex-1 flex flex-col relative p-4">
      {/* Botón arriba derecha */}
      <div className="absolute right-4 top-4">
        <button
          onClick={() => setOpenSheet(true)}
          className="px-4 py-2 rounded-xl text-white font-medium shadow-md hover:opacity-90 active:opacity-80"
          style={{ backgroundColor: PRIMARY }}
        >
          Report a Bug
        </button>
      </div>

      <h1 className="text-2xl font-bold mb-2">Bug Report</h1>

      {/* Nota sobre los indicadores (UK English) */}
      <p className="text-xs text-gray-500 mb-4">
        These indicators show whether the bug has been fixed and published to TestFlight, Play Store, or Apple Store.
      </p>

      {/* Lista global de reportes */}
      <div className="space-y-2">
        <h2 className="text-lg font-semibold">All recent reports</h2>
        {reports.length === 0 ? (
          <p className="text-sm text-gray-500">No reports yet.</p>
        ) : (
          <ul className="divide-y rounded-xl border overflow-hidden">
            {reports.map((r) => {
              const count = commentCounts[r.id] ?? 0;
              const tf = !!r.inTestflight;
              const ps = !!r.inPlayStore;
              const as = !!r.inAppleStore;
              return (
                <li
                  key={r.id}
                  className="p-4 flex items-center justify-between hover:bg-gray-50 cursor-pointer"
                  onClick={() => openDetailsFor(r)}
                >
                  <div className="min-w-0">
                    <div className="font-medium truncate">{r.title || 'Untitled'}</div>
                    <div className="text-xs text-gray-500">
                      {(r.operatingSystems || []).join(', ') || '—'} · {r.phoneModel || 'no model'}
                    </div>
                  </div>

                  <div className="flex items-center gap-4">
                    {/* Estados con nombres */}
                    <div className="flex items-center gap-3 text-xs">
                      <div className="flex items-center gap-1" title={`TestFlight: ${tf ? 'uploaded' : 'not uploaded'}`}>
                        <Fan size={18} className={tf ? 'text-green-600' : 'text-gray-300'} />
                        <span>TestFlight</span>
                      </div>
                      <div className="flex items-center gap-1" title={`Play Store: ${ps ? 'uploaded' : 'not uploaded'}`}>
                        <PlaySquare size={18} className={ps ? 'text-green-600' : 'text-gray-300'} />
                        <span>Play Store</span>
                      </div>
                      <div className="flex items-center gap-1" title={`Apple Store: ${as ? 'uploaded' : 'not uploaded'}`}>
                        <Apple size={18} className={as ? 'text-green-600' : 'text-gray-300'} />
                        <span>Apple Store</span>
                      </div>
                    </div>

                    {/* Solved/Pending */}
                    {r.solved ? (
                      <span className="text-xs px-2 py-1 rounded bg-green-100 text-green-700">Solved</span>
                    ) : (
                      <span className="text-xs px-2 py-1 rounded bg-yellow-100 text-yellow-700">Pending</span>
                    )}

                    {/* Cantidad de comentarios */}
                    <div className="flex items-center gap-1 text-xs text-gray-600" title="Comments">
                      <MessageSquare size={16} />
                      <span>{count}</span>
                    </div>

                    {/* Fecha */}
                    <span className="text-xs text-gray-400">
                      {r.createdAt?.toDate ? r.createdAt.toDate().toLocaleString() : '—'}
                    </span>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      {/* Backdrop del sheet */}
      {openSheet && (
        <div
          onClick={() => !submitting && setOpenSheet(false)}
          className="fixed inset-0 bg-black/40 z-40"
        />
      )}

      {/* Bottom Sheet: crear reporte */}
      <div
        className={`fixed left-0 right-0 bottom-0 z-50 transform transition-transform duration-300 ${
          openSheet ? 'translate-y-0' : 'translate-y-full'
        }`}
        aria-hidden={!openSheet}
      >
        <div className="mx-auto w-full max-w-3xl rounded-t-2xl bg-white shadow-2xl max-h-[85vh] flex flex-col">
          {/* Header fijo */}
          <div className="relative flex items-center justify-center p-4 border-b shrink-0">
            <div className="h-1.5 w-12 rounded-full bg-gray-200" />
            <button
              onClick={() => !submitting && setOpenSheet(false)}
              className="absolute right-4 top-3 text-gray-500 hover:text-gray-700"
              aria-label="Close"
            >
              ✕
            </button>
          </div>

          {/* Contenido scrolleable */}
          <div className="flex-1 overflow-y-auto overscroll-contain">
            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              {/* Title */}
              <div>
                <label className="block text-sm font-medium mb-1">Title *</label>
                <input
                  type="text"
                  className="w-full rounded-lg border border-gray-300 p-2"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                />
              </div>

              {/* Description */}
              <div>
                <label className="block text-sm font-medium mb-1">Description *</label>
                <textarea
                  className="w-full rounded-lg border border-gray-300 p-2 h-28 resize-y"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                />
              </div>

              {/* Operating Systems */}
              <div>
                <span className="block text-sm font-medium mb-2">Operating Systems *</span>
                <div className="flex flex-wrap gap-2">
                  {OS_OPTIONS.map((opt) => {
                    const active = osSelected.includes(opt.key);
                    return (
                      <button
                        key={opt.key}
                        type="button"
                        onClick={() => toggleOS(opt.key)}
                        className={`px-3 py-1.5 rounded-full border text-sm ${
                          active
                            ? 'bg-gray-900 text-white border-gray-900'
                            : 'bg-white text-gray-700 border-gray-300 hover:border-gray-400'
                        }`}
                      >
                        {opt.label}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Phone model */}
              <div>
                <label className="block text-sm font-medium mb-1">Phone model (optional)</label>
                <input
                  type="text"
                  className="w-full rounded-lg border border-gray-300 p-2"
                  value={phoneModel}
                  onChange={(e) => setPhoneModel(e.target.value)}
                />
              </div>

              {/* Media inputs */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Images</label>
                  <input type="file" accept="image/*" multiple onChange={handleFilesChange} />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Video</label>
                  <input type="file" accept="video/*" onChange={handleVideoChange} />
                </div>
              </div>

              {/* Previews */}
              {(imagePreviews.length > 0 || videoPreview) && (
                <div className="space-y-6">
                  {/* Grid imágenes */}
                  {imagePreviews.length > 0 && (
                    <div>
                      <h4 className="text-sm font-semibold mb-2">Image preview</h4>
                      <div className="grid grid-cols-3 sm:grid-cols-4 gap-3">
                        {imagePreviews.map((url, idx) => (
                          <div key={url} className="relative group">
                            <img
                              src={url}
                              alt={`preview-${idx}`}
                              className="h-24 w-full object-cover rounded-lg border"
                            />
                            <button
                              type="button"
                              onClick={() => removeImageAt(idx)}
                              className="absolute top-1 right-1 rounded-full bg-black/60 text-white text-xs px-2 py-1 opacity-0 group-hover:opacity-100 transition"
                            >
                              Remove
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Video preview 300x300 */}
                  {videoPreview && (
                    <div>
                      <h4 className="text-sm font-semibold mb-2">Video preview</h4>
                      <div className="relative w-[300px] h-[300px]">
                        <video
                          src={videoPreview}
                          controls
                          className="w-[300px] h-[300px] object-cover rounded-lg border"
                        />
                        <button
                          type="button"
                          onClick={clearVideo}
                          className="absolute top-2 right-2 rounded-full bg-black/60 text-white text-xs px-2 py-1"
                        >
                          Remove
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Actions */}
              <div className="flex items-center justify-end gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => !submitting && setOpenSheet(false)}
                  className="px-4 py-2 rounded-lg border border-gray-300 text-gray-700"
                  disabled={submitting}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={!canSubmit || submitting}
                  className={`px-4 py-2 rounded-lg font-medium shadow-md ${
                    !canSubmit || submitting
                      ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                      : 'text-white hover:opacity-90 active:opacity-80'
                  }`}
                  style={(!canSubmit || submitting) ? {} : { backgroundColor: PRIMARY }}
                >
                  {submitting ? 'Submitting…' : 'Submit Bug'}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>

      {/* MODAL DETALLE + COMENTARIOS (global) */}
      {detailsOpen && activeReport && (
        <>
          <div
            className="fixed inset-0 bg-black/40 z-50"
            onClick={() => setDetailsOpen(false)}
          />
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <div className="w-full max-w-3xl bg-white rounded-2xl shadow-2xl max-h-[90vh] flex flex-col">
              {/* Header */}
              <div className="flex items-center justify-between p-4 border-b">
                <h3 className="text-lg font-semibold">Bug details</h3>
                <button
                  onClick={() => setDetailsOpen(false)}
                  className="text-gray-600 hover:text-gray-800"
                >
                  ✕
                </button>
              </div>

              {/* Body (scroll) */}
              <div className="flex-1 overflow-y-auto p-6 space-y-6">
                {/* Datos del bug */}
                <section className="space-y-2">
                  <div className="text-xl font-bold">{activeReport.title || 'Untitled'}</div>
                  <div className="text-sm text-gray-600">
                    {activeReport.createdAt?.toDate
                      ? activeReport.createdAt.toDate().toLocaleString()
                      : '—'}
                  </div>
                  <p className="text-gray-800 whitespace-pre-wrap">{activeReport.description}</p>

                  <div className="flex flex-wrap items-center gap-2 text-sm">
                    <span className="px-2 py-1 rounded bg-gray-100">
                      OS: {(activeReport.operatingSystems || []).join(', ') || '—'}
                    </span>
                    <span className="px-2 py-1 rounded bg-gray-100">
                      Model: {activeReport.phoneModel || '—'}
                    </span>
                    <span
                      className={`px-2 py-1 rounded ${activeReport.solved ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'}`}
                    >
                      {activeReport.solved ? 'Solved' : 'Pending'}
                    </span>

                    {/* Estados con iconos + nombres */}
                    <span className="inline-flex items-center gap-1 px-2 py-1 rounded bg-gray-100">
                      <Fan size={16} className={activeReport.inTestflight ? 'text-green-600' : 'text-gray-300'} />
                      <span className="text-xs">TestFlight</span>
                    </span>
                    <span className="inline-flex items-center gap-1 px-2 py-1 rounded bg-gray-100">
                      <PlaySquare size={16} className={activeReport.inPlayStore ? 'text-green-600' : 'text-gray-300'} />
                      <span className="text-xs">Play Store</span>
                    </span>
                    <span className="inline-flex items-center gap-1 px-2 py-1 rounded bg-gray-100">
                      <Apple size={16} className={activeReport.inAppleStore ? 'text-green-600' : 'text-gray-300'} />
                      <span className="text-xs">Apple Store</span>
                    </span>
                  </div>

                  {/* Media vista rápida */}
                  {(activeReport.imageUrls?.length || activeReport.videoUrl) && (
                    <div className="pt-3 space-y-3">
                      {activeReport.imageUrls?.length > 0 && (
                        <div>
                          <div className="text-sm font-semibold mb-2">Images</div>
                          <div className="grid grid-cols-3 sm:grid-cols-4 gap-3">
                            {activeReport.imageUrls.map((u, i) => (
                              <a
                                key={u}
                                href={u}
                                target="_blank"
                                rel="noreferrer"
                                className="block"
                              >
                                <img
                                  src={u}
                                  alt={`img-${i}`}
                                  className="h-24 w-full object-cover rounded-lg border"
                                />
                              </a>
                            ))}
                          </div>
                        </div>
                      )}
                      {activeReport.videoUrl && (
                        <div>
                          <div className="text-sm font-semibold mb-2">Video</div>
                          <video
                            src={activeReport.videoUrl}
                            controls
                            className="w-[300px] h-[300px] object-cover rounded-lg border"
                          />
                        </div>
                      )}
                    </div>
                  )}
                </section>

                {/* Comentarios (cronológicos) */}
                <section className="space-y-3">
                  <div className="flex items-center justify-between">
                    <h4 className="text-md font-semibold">Comments</h4>
                    <div className="flex items-center gap-1 text-xs text-gray-600">
                      <MessageSquare size={16} />
                      <span>{commentCounts[activeReport.id] ?? comments.length}</span>
                    </div>
                  </div>

                  {comments.length === 0 ? (
                    <p className="text-sm text-gray-500">No comments yet.</p>
                  ) : (
                    <ul className="space-y-2">
                      {comments.map((c) => (
                        <li key={c.id} className="rounded-lg border p-3">
                          <div className="text-xs text-gray-500 flex items-center justify-between">
                            <span>{c.authorEmail || 'Unknown'}</span>
                            <div className="flex items-center gap-2">
                              <span>
                                {c.createdAt?.toDate
                                  ? c.createdAt.toDate().toLocaleString()
                                  : '—'}
                              </span>
                              {/* Botón eliminar solo si es del usuario logueado */}
                              {user?.uid && c.authorUid === user.uid && (
                                <button
                                  onClick={() => requestDeleteComment(c.id, c.authorUid)}
                                  className="text-red-600 hover:text-red-700 text-xs font-semibold"
                                  title="Delete comment"
                                >
                                  Delete
                                </button>
                              )}
                            </div>
                          </div>
                          <p className="text-sm mt-1 whitespace-pre-wrap">{c.text}</p>
                        </li>
                      ))}
                    </ul>
                  )}

                  {/* Input para mandar comentario */}
                  <div className="flex items-center gap-2 pt-2">
                    <input
                      type="text"
                      value={newComment}
                      onChange={(e) => setNewComment(e.target.value)}
                      placeholder="Write a comment…"
                      className="flex-1 rounded-lg border border-gray-300 p-2"
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          sendComment();
                        }
                      }}
                    />
                    <button
                      onClick={sendComment}
                      disabled={!newComment.trim() || sendingComment}
                      className={`px-4 py-2 rounded-lg font-medium shadow-md ${
                        !newComment.trim() || sendingComment
                          ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                          : 'text-white hover:opacity-90 active:opacity-80'
                      }`}
                      style={(!newComment.trim() || sendingComment) ? {} : { backgroundColor: PRIMARY }}
                    >
                      {sendingComment ? 'Sending…' : 'Send'}
                    </button>
                  </div>
                </section>
              </div>
            </div>
          </div>
        </>
      )}

      {/* MODAL CONFIRMACIÓN ELIMINAR COMENTARIO */}
      {confirmDelete && (
        <>
          <div
            className="fixed inset-0 z-[60] bg-black/50"
            onClick={() => !deletingComment && setConfirmDelete(null)}
          />
          <div className="fixed inset-0 z-[61] flex items-center justify-center p-4">
            <div className="w-full max-w-sm bg-white rounded-xl shadow-2xl p-6">
              <h4 className="text-base font-semibold mb-2">Delete comment</h4>
              <p className="text-sm text-gray-600 mb-4">
                Are you sure you want to delete this comment? This action cannot be undone.
              </p>
              <div className="flex justify-end gap-2">
                <button
                  onClick={() => setConfirmDelete(null)}
                  disabled={deletingComment}
                  className="px-3 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-60"
                >
                  Cancel
                </button>
                <button
                  onClick={confirmDeleteComment}
                  disabled={deletingComment}
                  className="px-3 py-2 rounded-lg text-white shadow-md disabled:opacity-60"
                  style={{ backgroundColor: '#e53935' }}
                >
                  {deletingComment ? 'Deleting…' : 'Delete'}
                </button>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default BugReport;